/**
 * Self-test suite for the message-request gating feature.
 *
 * Each case exercises one transition or one policy decision so a single FAIL
 * line points at the specific broken invariant rather than a vague "message
 * requests are broken." Suite is split into three layers mirroring the iOS
 * `MessageRequestSelfTest.swift`:
 *
 *   A. State machine via observe() and manual transitions
 *      (MessageRequestStore-level cases, unique-DB scoped).
 *
 *   B. Policy decisions (MessageRequestPolicy-level cases, pure functions).
 *
 *   C. Side-effects on the contact roster.
 *
 * Cases:
 *
 *   A. State machine
 *    1. First observe with isContact-via-roster=true → render, no record
 *    2. First observe with no roster entry → requestBanner, requested record
 *    3. Repeated observe on requested → counter++, preview refreshed
 *    4. accept() from requested → accepted + roster entry added
 *    5. observe after accept → render, counter++
 *    6. block() from requested → blocked, roster entry absent
 *    7. block() from accepted → blocked, roster entry removed
 *    8. observe on blocked → suppress, blockedAttemptCount++
 *    9. unblock() from blocked → requested, counters preserved, roster absent
 *   10. delete() from requested → record gone; next observe fresh requested
 *   11. delete() from accepted → record gone + roster entry removed
 *   12. delete() from blocked → record gone but next observe is requested
 *   13. accept() with no prior record creates accepted directly + roster
 *   14. block() with no prior record creates blocked directly (pre-emptive)
 *   15. unblock() on missing record throws notFound
 *   16. unblock() on requested throws invalidTransition
 *   17. allRequested() returns only requested, sorted lastMessageAt desc
 *   18. allBlocked() returns only blocked userIds
 *   19. Persistence: state survives a fresh store at the same db name
 *   20. Preview snippet truncates to MESSAGE_REQUEST_PREVIEW_MAX_LENGTH chars
 *
 *   B. Policy
 *   21. shouldSendDeliveryReceipt: true for null/accepted, false for requested/blocked
 *   22. shouldSendReadReceipt: same matrix
 *   23. shouldSendTypingIndicator: same matrix
 *   24. shouldRender: true for null/accepted, false for requested/blocked
 *   25. shouldSurfaceAsRequest: true only for requested
 *
 *   C. Roster
 *   26. markContact then observe → render even with no record
 *   27. unmarkContact + accepted record → still render via state, but
 *       a fresh delete()+observe cycle would gate (roster = source of truth
 *       only when no record exists)
 */

import { SelfTestRunner, buildSuiteResult, fail, pass } from "../selftest";
import type { TestCase } from "../selftest";
import {
  MessageRequestStore,
  type MessageRequestState,
} from "./MessageRequestStore";
import { MessageRequestPolicy } from "./MessageRequestPolicy";
import {
  MESSAGE_REQUEST_PREVIEW_MAX_LENGTH,
  MessageRequestNotFoundError,
  MessageRequestInvalidTransitionError,
  type MessageRequestRecord,
} from "./MessageRequest";

SelfTestRunner.register({
  name: "Message requests (first-message gating)",
  isVerified: true,
  run: async () =>
    buildSuiteResult([
      // A. State machine
      await testFirstObserveContact(),
      await testFirstObserveStranger(),
      await testRepeatedObserveBumpsCounter(),
      await testAcceptFromRequested(),
      await testObserveAfterAcceptRenders(),
      await testBlockFromRequested(),
      await testBlockFromAcceptedRemovesFromRoster(),
      await testObserveOnBlockedSuppresses(),
      await testUnblockFromBlocked(),
      await testDeleteFromRequested(),
      await testDeleteFromAcceptedClearsRoster(),
      await testDeleteFromBlockedReengagesGate(),
      await testAcceptWithNoPriorRecord(),
      await testBlockWithNoPriorRecord(),
      await testUnblockMissingRejected(),
      await testUnblockOnRequestedRejected(),
      await testAllRequestedSorting(),
      await testAllBlocked(),
      await testPersistence(),
      await testPreviewTruncation(),
      // B. Policy
      await testPolicyDeliveryReceipt(),
      await testPolicyReadReceipt(),
      await testPolicyTypingIndicator(),
      await testPolicyShouldRender(),
      await testPolicyShouldSurfaceAsRequest(),
      // C. Roster
      await testMarkContactBypassesGate(),
      await testUnmarkContactDoesNotDemoteAccepted(),
    ]),
});

// ─── Fixtures ──────────────────────────────────────────────────────────────

/** Each test gets an isolated DB by using a UUID-named database. */
function freshStore(): MessageRequestStore {
  const id = crypto.randomUUID();
  return new MessageRequestStore(`kaether_mr_test_${id}`);
}

function makeRecord(state: MessageRequestState): MessageRequestRecord {
  return {
    userId: "x",
    state,
    firstSeenAt: 0,
    lastMessageAt: 0,
    messageCount: 0,
    previewSnippet: null,
    blockedAttemptCount: 0,
  };
}

// ─── A. State machine ──────────────────────────────────────────────────────

async function testFirstObserveContact(): Promise<TestCase> {
  const name = "first observe from contact-in-roster → render, no record";
  const s = freshStore();
  try {
    await s.markContact("alice");
    const d = await s.observe("alice", "hi");
    const record = await s.get("alice");
    const ok = d.kind === "render" && record === null;
    return ok
      ? pass(name)
      : fail(name, `disp=${d.kind} record=${JSON.stringify(record)}`);
  } finally {
    await s.dropDatabase();
  }
}

async function testFirstObserveStranger(): Promise<TestCase> {
  const name = "first observe from stranger → requestBanner, requested record";
  const s = freshStore();
  try {
    const d = await s.observe("stranger", "hello there");
    if (d.kind !== "requestBanner") {
      return fail(name, `disp=${d.kind}`);
    }
    const r = d.record;
    const ok =
      r.state === "requested" &&
      r.userId === "stranger" &&
      r.messageCount === 1 &&
      r.previewSnippet === "hello there" &&
      r.blockedAttemptCount === 0;
    return ok ? pass(name) : fail(name, `record=${JSON.stringify(r)}`);
  } finally {
    await s.dropDatabase();
  }
}

async function testRepeatedObserveBumpsCounter(): Promise<TestCase> {
  const name =
    "repeated observe bumps counter, refreshes preview & lastMessageAt";
  const s = freshStore();
  try {
    await s.observe("stranger", "first", 1000);
    const d = await s.observe("stranger", "second", 2000);
    if (d.kind !== "requestBanner") {
      return fail(name, `disp=${d.kind}`);
    }
    const r = d.record;
    const ok =
      r.messageCount === 2 &&
      r.previewSnippet === "second" &&
      r.lastMessageAt === 2000 &&
      r.firstSeenAt === 1000;
    return ok ? pass(name) : fail(name, `record=${JSON.stringify(r)}`);
  } finally {
    await s.dropDatabase();
  }
}

async function testAcceptFromRequested(): Promise<TestCase> {
  const name = "accept from requested → accepted + roster";
  const s = freshStore();
  try {
    await s.observe("stranger", "hi");
    const r = await s.accept("stranger");
    const inRoster = await s.isContact("stranger");
    const ok = r.state === "accepted" && inRoster && r.previewSnippet === null;
    return ok
      ? pass(name)
      : fail(name, `record=${JSON.stringify(r)} inRoster=${inRoster}`);
  } finally {
    await s.dropDatabase();
  }
}

async function testObserveAfterAcceptRenders(): Promise<TestCase> {
  const name = "observe after accept → render and counter increments";
  const s = freshStore();
  try {
    await s.observe("stranger", "hi");
    await s.accept("stranger");
    const d = await s.observe("stranger", "follow-up");
    const record = await s.get("stranger");
    const ok =
      d.kind === "render" &&
      record?.state === "accepted" &&
      record?.messageCount === 2;
    return ok
      ? pass(name)
      : fail(name, `disp=${d.kind} record=${JSON.stringify(record)}`);
  } finally {
    await s.dropDatabase();
  }
}

async function testBlockFromRequested(): Promise<TestCase> {
  const name = "block from requested → blocked, no roster entry";
  const s = freshStore();
  try {
    await s.observe("stranger", "hi");
    const r = await s.block("stranger");
    const inRoster = await s.isContact("stranger");
    const ok = r.state === "blocked" && !inRoster;
    return ok ? pass(name) : fail(name, `record=${JSON.stringify(r)}`);
  } finally {
    await s.dropDatabase();
  }
}

async function testBlockFromAcceptedRemovesFromRoster(): Promise<TestCase> {
  const name = "block from accepted → blocked, roster entry removed";
  const s = freshStore();
  try {
    await s.observe("stranger", "hi");
    await s.accept("stranger");
    if (!(await s.isContact("stranger"))) {
      return fail(name, "accept did not seed roster");
    }
    const r = await s.block("stranger");
    const inRoster = await s.isContact("stranger");
    const ok = r.state === "blocked" && !inRoster;
    return ok
      ? pass(name)
      : fail(name, `record=${JSON.stringify(r)} inRoster=${inRoster}`);
  } finally {
    await s.dropDatabase();
  }
}

async function testObserveOnBlockedSuppresses(): Promise<TestCase> {
  const name = "observe on blocked suppresses, increments blockedAttemptCount";
  const s = freshStore();
  try {
    await s.block("stranger");
    const d1 = await s.observe("stranger", "spam1");
    const d2 = await s.observe("stranger", "spam2");
    const r = await s.get("stranger");
    const ok =
      d1.kind === "suppress" &&
      d2.kind === "suppress" &&
      r?.state === "blocked" &&
      r?.blockedAttemptCount === 2 &&
      r?.messageCount === 2;
    return ok
      ? pass(name)
      : fail(name, `d1=${d1.kind} d2=${d2.kind} record=${JSON.stringify(r)}`);
  } finally {
    await s.dropDatabase();
  }
}

async function testUnblockFromBlocked(): Promise<TestCase> {
  const name =
    "unblock from blocked → requested, counters preserved, no roster";
  const s = freshStore();
  try {
    await s.observe("stranger", "hi");
    await s.block("stranger");
    await s.observe("stranger", "hammered");
    const r = await s.unblock("stranger");
    const inRoster = await s.isContact("stranger");
    const ok =
      r.state === "requested" &&
      r.messageCount === 2 &&
      r.blockedAttemptCount === 0 &&
      !inRoster;
    return ok ? pass(name) : fail(name, `record=${JSON.stringify(r)}`);
  } finally {
    await s.dropDatabase();
  }
}

async function testDeleteFromRequested(): Promise<TestCase> {
  const name = "delete from requested → record gone, fresh request next time";
  const s = freshStore();
  try {
    await s.observe("stranger", "hi");
    await s.delete("stranger");
    const after = await s.get("stranger");
    const d = await s.observe("stranger", "again");
    if (d.kind !== "requestBanner") {
      return fail(name, `next observe disp=${d.kind}`);
    }
    const ok = after === null;
    return ok
      ? pass(name)
      : fail(name, `record after delete: ${JSON.stringify(after)}`);
  } finally {
    await s.dropDatabase();
  }
}

async function testDeleteFromAcceptedClearsRoster(): Promise<TestCase> {
  const name = "delete from accepted → record gone + roster cleared";
  const s = freshStore();
  try {
    await s.observe("stranger", "hi");
    await s.accept("stranger");
    await s.delete("stranger");
    const inRoster = await s.isContact("stranger");
    const record = await s.get("stranger");
    const ok = record === null && !inRoster;
    return ok
      ? pass(name)
      : fail(name, `record=${JSON.stringify(record)} inRoster=${inRoster}`);
  } finally {
    await s.dropDatabase();
  }
}

async function testDeleteFromBlockedReengagesGate(): Promise<TestCase> {
  const name = "delete from blocked → fresh requested on next message";
  const s = freshStore();
  try {
    await s.block("stranger");
    await s.delete("stranger");
    const d = await s.observe("stranger", "back");
    if (d.kind !== "requestBanner") {
      return fail(name, `disp=${d.kind}`);
    }
    const ok = d.record.state === "requested";
    return ok ? pass(name) : fail(name, `record=${JSON.stringify(d.record)}`);
  } finally {
    await s.dropDatabase();
  }
}

async function testAcceptWithNoPriorRecord(): Promise<TestCase> {
  const name = "accept with no prior record creates accepted + roster";
  const s = freshStore();
  try {
    const r = await s.accept("explicitfriend");
    const inRoster = await s.isContact("explicitfriend");
    const ok = r.state === "accepted" && inRoster;
    return ok
      ? pass(name)
      : fail(name, `record=${JSON.stringify(r)} inRoster=${inRoster}`);
  } finally {
    await s.dropDatabase();
  }
}

async function testBlockWithNoPriorRecord(): Promise<TestCase> {
  const name = "block with no prior record creates blocked directly";
  const s = freshStore();
  try {
    const r = await s.block("stranger");
    const inRoster = await s.isContact("stranger");
    const ok = r.state === "blocked" && r.messageCount === 0 && !inRoster;
    return ok ? pass(name) : fail(name, `record=${JSON.stringify(r)}`);
  } finally {
    await s.dropDatabase();
  }
}

async function testUnblockMissingRejected(): Promise<TestCase> {
  const name = "unblock on missing record throws notFound";
  const s = freshStore();
  try {
    await s.unblock("ghost");
    return fail(name, "did not throw");
  } catch (e) {
    if (e instanceof MessageRequestNotFoundError) {
      return pass(name);
    }
    return fail(name, `wrong error: ${e}`);
  } finally {
    await s.dropDatabase();
  }
}

async function testUnblockOnRequestedRejected(): Promise<TestCase> {
  const name = "unblock on requested throws invalidTransition";
  const s = freshStore();
  try {
    await s.observe("stranger", "hi");
    await s.unblock("stranger");
    return fail(name, "did not throw");
  } catch (e) {
    if (
      e instanceof MessageRequestInvalidTransitionError &&
      e.from === "requested" &&
      e.action === "unblock"
    ) {
      return pass(name);
    }
    return fail(name, `wrong error: ${e}`);
  } finally {
    await s.dropDatabase();
  }
}

async function testAllRequestedSorting(): Promise<TestCase> {
  const name = "allRequested returns requested only, sorted lastMessageAt desc";
  const s = freshStore();
  try {
    await s.observe("alice", "first", 1000);
    await s.observe("bob", "second", 2000);
    await s.observe("carol", "third", 3000);
    await s.accept("carol"); // carol leaves the requested set
    const ids = (await s.allRequested()).map((r) => r.userId);
    const ok = ids[0] === "bob" && ids[1] === "alice" && ids.length === 2;
    return ok
      ? pass(name)
      : fail(name, `got ${JSON.stringify(ids)}, want ["bob","alice"]`);
  } finally {
    await s.dropDatabase();
  }
}

async function testAllBlocked(): Promise<TestCase> {
  const name = "allBlocked returns exactly the blocked userIds";
  const s = freshStore();
  try {
    await s.observe("alice", "x");
    await s.block("bob");
    await s.block("carol");
    const ids = new Set(await s.allBlocked());
    const ok = ids.has("bob") && ids.has("carol") && ids.size === 2;
    return ok
      ? pass(name)
      : fail(
          name,
          `got ${JSON.stringify([...ids].sort())}, want ["bob","carol"]`,
        );
  } finally {
    await s.dropDatabase();
  }
}

async function testPersistence(): Promise<TestCase> {
  const name = "state + roster persist across store instances";
  const dbName = `kaether_mr_test_persist_${crypto.randomUUID()}`;
  const writer = new MessageRequestStore(dbName);
  try {
    await writer.observe("stranger", "hi");
    await writer.accept("alice-friend");
    await writer.block("bob-blocked");

    // Open a fresh store instance against the SAME database.
    const reader = new MessageRequestStore(dbName);
    const strangerOK = (await reader.get("stranger"))?.state === "requested";
    const aliceFriend = await reader.get("alice-friend");
    const aliceOK =
      aliceFriend?.state === "accepted" &&
      (await reader.isContact("alice-friend"));
    const bobBlocked = await reader.get("bob-blocked");
    const bobOK =
      bobBlocked?.state === "blocked" &&
      !(await reader.isContact("bob-blocked"));
    await reader.dropDatabase();

    const ok = strangerOK && aliceOK && bobOK;
    return ok
      ? pass(name)
      : fail(
          name,
          `stranger=${strangerOK} alice=${aliceOK} bob=${bobOK}`,
        );
  } finally {
    // writer may already be closed by reader.dropDatabase above (same db),
    // so just suppress errors.
    try {
      await writer.dropDatabase();
    } catch {
      /* ignore */
    }
  }
}

async function testPreviewTruncation(): Promise<TestCase> {
  const name =
    "preview snippet truncates to MESSAGE_REQUEST_PREVIEW_MAX_LENGTH chars + ellipsis";
  const s = freshStore();
  try {
    const long = "A".repeat(MESSAGE_REQUEST_PREVIEW_MAX_LENGTH + 50);
    const d = await s.observe("stranger", long);
    if (d.kind !== "requestBanner") {
      return fail(name, `disp=${d.kind}`);
    }
    const snippet = d.record.previewSnippet;
    // Truncated: MESSAGE_REQUEST_PREVIEW_MAX_LENGTH chars of "A" + "…" (1 codepoint = 1 JS char)
    const ok =
      snippet !== null &&
      snippet.length === MESSAGE_REQUEST_PREVIEW_MAX_LENGTH + 1 &&
      snippet.endsWith("…");
    return ok
      ? pass(name)
      : fail(
          name,
          `snippet=${snippet} count=${snippet?.length ?? -1}`,
        );
  } finally {
    await s.dropDatabase();
  }
}

// ─── B. Policy ─────────────────────────────────────────────────────────────

async function testPolicyDeliveryReceipt(): Promise<TestCase> {
  const name = "shouldSendDeliveryReceipt across all states";
  const cases: Array<[MessageRequestRecord | null, boolean]> = [
    [null, true],
    [makeRecord("accepted"), true],
    [makeRecord("requested"), false],
    [makeRecord("blocked"), false],
  ];
  for (const [r, expected] of cases) {
    const got = MessageRequestPolicy.shouldSendDeliveryReceipt(r);
    if (got !== expected) {
      return fail(
        name,
        `state=${r?.state ?? "null"} got=${got} want=${expected}`,
      );
    }
  }
  return pass(name);
}

async function testPolicyReadReceipt(): Promise<TestCase> {
  const name = "shouldSendReadReceipt across all states";
  const cases: Array<[MessageRequestRecord | null, boolean]> = [
    [null, true],
    [makeRecord("accepted"), true],
    [makeRecord("requested"), false],
    [makeRecord("blocked"), false],
  ];
  for (const [r, expected] of cases) {
    const got = MessageRequestPolicy.shouldSendReadReceipt(r);
    if (got !== expected) {
      return fail(
        name,
        `state=${r?.state ?? "null"} got=${got} want=${expected}`,
      );
    }
  }
  return pass(name);
}

async function testPolicyTypingIndicator(): Promise<TestCase> {
  const name = "shouldSendTypingIndicator across all states";
  const cases: Array<[MessageRequestRecord | null, boolean]> = [
    [null, true],
    [makeRecord("accepted"), true],
    [makeRecord("requested"), false],
    [makeRecord("blocked"), false],
  ];
  for (const [r, expected] of cases) {
    const got = MessageRequestPolicy.shouldSendTypingIndicator(r);
    if (got !== expected) {
      return fail(
        name,
        `state=${r?.state ?? "null"} got=${got} want=${expected}`,
      );
    }
  }
  return pass(name);
}

async function testPolicyShouldRender(): Promise<TestCase> {
  const name = "shouldRender across all states";
  const cases: Array<[MessageRequestRecord | null, boolean]> = [
    [null, true],
    [makeRecord("accepted"), true],
    [makeRecord("requested"), false],
    [makeRecord("blocked"), false],
  ];
  for (const [r, expected] of cases) {
    const got = MessageRequestPolicy.shouldRender(r);
    if (got !== expected) {
      return fail(
        name,
        `state=${r?.state ?? "null"} got=${got} want=${expected}`,
      );
    }
  }
  return pass(name);
}

async function testPolicyShouldSurfaceAsRequest(): Promise<TestCase> {
  const name = "shouldSurfaceAsRequest only for requested";
  const cases: Array<[MessageRequestRecord | null, boolean]> = [
    [null, false],
    [makeRecord("accepted"), false],
    [makeRecord("requested"), true],
    [makeRecord("blocked"), false],
  ];
  for (const [r, expected] of cases) {
    const got = MessageRequestPolicy.shouldSurfaceAsRequest(r);
    if (got !== expected) {
      return fail(
        name,
        `state=${r?.state ?? "null"} got=${got} want=${expected}`,
      );
    }
  }
  return pass(name);
}

// ─── C. Roster ──────────────────────────────────────────────────────────────

async function testMarkContactBypassesGate(): Promise<TestCase> {
  const name = "markContact bypasses gate (no record created)";
  const s = freshStore();
  try {
    await s.markContact("alice");
    const d = await s.observe("alice", "hi");
    const record = await s.get("alice");
    const ok = d.kind === "render" && record === null;
    return ok
      ? pass(name)
      : fail(name, `disp=${d.kind} record=${JSON.stringify(record)}`);
  } finally {
    await s.dropDatabase();
  }
}

async function testUnmarkContactDoesNotDemoteAccepted(): Promise<TestCase> {
  const name = "unmarkContact leaves accepted record authoritative";
  const s = freshStore();
  try {
    await s.observe("alice", "hi");
    await s.accept("alice");
    await s.unmarkContact("alice");
    // Roster cleared, but the accepted record should still drive render.
    const d = await s.observe("alice", "again");
    const record = await s.get("alice");
    const inRoster = await s.isContact("alice");
    const ok =
      d.kind === "render" && record?.state === "accepted" && !inRoster;
    return ok
      ? pass(name)
      : fail(
          name,
          `disp=${d.kind} record=${JSON.stringify(record)} inRoster=${inRoster}`,
        );
  } finally {
    await s.dropDatabase();
  }
}
